import { useTranslation } from 'react-i18next';

import { ProjectResponse } from '@/app/models/projects';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { Box, Checkbox, FormControlLabel, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import PrevisionPeriod from './PrevisionPeriod';

interface GenerateReportScreenProps {
  setDrawerSendAttachmentEight: (open: boolean) => void;
  selectProjects: ProjectResponse[];
  setSelectProjects: (id: ProjectResponse[]) => void;
  handleGenerateProject: CallableFunction;
  isDisabled: boolean;
}

export default function GenerateReportScreen(props: GenerateReportScreenProps) {
  const theme = useTheme();
  const { t } = useTranslation(['generateReport', 'common']);
  const { setDrawerSendAttachmentEight, selectProjects, handleGenerateProject, setSelectProjects, isDisabled } = props;

  const [initPeriod, setInitPeriod] = useState<dayjs.Dayjs | null>(dayjs());
  const [endPeriod, setEndPeriod] = useState<dayjs.Dayjs | null>(dayjs());

  const [checked, setChecked] = useState(false);

  const handleCancel = () => {
    setDrawerSendAttachmentEight(false);
  };

  const handleCheckedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const handleSubmit = async () => {
    handleGenerateProject(initPeriod, endPeriod, checked);
    setDrawerSendAttachmentEight(false);
    setSelectProjects([]);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('generate.report')} />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 4,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 4 }}>
            <Typography variant="fieldHeader" sx={{ mt: 2, display: 'flex', gap: 4, alignItems: 'center' }}>
              {t('common:project')}
              <Box>
                {selectProjects.map((project) => (
                  <Box key={project.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        bgcolor: 'grey.700',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                      }}
                    />
                    <Typography>{`${project.code} - ${project.name}`}</Typography>
                  </Box>
                ))}
              </Box>
            </Typography>
          </Box>
        </Box>
        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2 }} />
        <Box sx={{ ml: 1, mt: 2, display: 'flex', gap: 2, justifyItems: 'center', alignItems: 'center' }}>
          <Typography variant="fieldHeader" sx={{ mt: 1 }}>
            {t('common:period')}
          </Typography>
          <PrevisionPeriod
            initPeriod={initPeriod}
            setInitPeriod={setInitPeriod}
            endPeriod={endPeriod}
            setEndPeriod={setEndPeriod}
          />
        </Box>
        <FormControlLabel
          control={<Checkbox checked={checked} onChange={handleCheckedChange} color="secondary" />}
          label={t('lgpd.notice')}
          sx={{
            display: 'flex',
            m: 4,
            '& .MuiFormControlLabel-label': {
              fontSize: '1rem',
              fontWeight: 500,
            },
          }}
        />
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons
          onSave={handleSubmit}
          onCancel={handleCancel}
          labelSave={t('generate') as string}
          disabled={isDisabled}
        />
      </Box>
    </Box>
  );
}
